import { useState } from 'react';
import { C, SERIF, mono } from '../theme';
import { Icon } from '../icons';
import { Pill } from '../components/Pill';
import { MonthStepper } from '../components/MonthStepper';
import { Loading, Empty } from '../components/States';
import { useToast } from '../components/Toast';
import { api } from '../api';
import type { Overview, TenancyRow } from '../api';
import { useAsync } from '../useAsync';
import { inr, currentPeriod, fmtDate } from '../format';
import { unitDesc, dateBarColor, dateCount, rowPill } from './helpers';
import { openWhatsApp, rentReminder } from '../whatsapp';
import { SetupCard } from './SetupCard';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A tenant whose rent is still open — a candidate for a one-tap nudge. */
function nudgeList(tenancies: TenancyRow[]): TenancyRow[] {
  return tenancies
    .filter((t) => (t.lifecycle ?? 'active') === 'active' && (t.balance ?? 0) > 0 && (t.status === 'overdue' || (t.dueInDays ?? 99) <= 5))
    .sort((a, b) => (a.dueInDays ?? 0) - (b.dueInDays ?? 0));
}

export function OwnerOverview({
  propertyId,
  onOpen,
  onOpenSheet,
}: {
  propertyId: string;
  onOpen: (tenancyId: string) => void;
  /** Open a tenancy straight into a sheet (Finish setting up). */
  onOpenSheet?: (tenancyId: string, sheet?: string) => void;
}) {
  const notify = useToast();
  const [period, setPeriod] = useState<string | null>(null);
  const { data, loading, error, reload } = useAsync<Overview>(
    () => api.overview(propertyId, period ?? undefined),
    [propertyId, period],
  );

  if (loading && !data) return <Loading label="Loading overview…" minHeight={460} />;
  if (error || !data) {
    return (
      <Empty icon="grid" title="Overview unavailable" hint="We couldn't reach the property overview. The API may be starting up." minHeight={460}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  }

  const { property, stats, dates, tenancies, arrears } = data;
  const shownPeriod = period ?? data.period ?? currentPeriod();
  const propName = property?.name ?? 'Property';
  const expected = stats?.expected ?? 0;
  const collected = stats?.collected ?? 0;
  const pct = expected > 0 ? Math.min(100, Math.round((collected / expected) * 100)) : 0;
  const [py, pm] = shownPeriod.split('-').map(Number);
  const monthLabel = `${MONTHS[(pm ?? 1) - 1]} ${py}`;

  const toSend = nudgeList(tenancies);

  function nudge(t: TenancyRow) {
    const overdue = (t.dueInDays ?? 0) < 0 || t.status === 'overdue';
    const msg = rentReminder({
      property: propName,
      tenant: t.tenantName,
      amount: inr(t.balance ?? t.netExpected ?? t.rentAmount),
      dueLabel: t.dueDate ? fmtDate(t.dueDate) : `the ${t.dueDay ?? 5}th`,
      overdue,
    });
    if (!openWhatsApp(t.tenantPhone, msg)) {
      notify('Add the tenant’s phone (Invite tenant) to send on WhatsApp');
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
        <div>
          <h2 style={{ fontFamily: SERIF, fontSize: 24, margin: '0 0 2px', fontWeight: 600 }}>{propName}</h2>
          <div style={{ color: C.muted, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon n="pin" size={15} /> {[property?.city, property?.state].filter(Boolean).join(', ') || '–'} · {stats?.units ?? tenancies.length} units
          </div>
        </div>
        <MonthStepper period={shownPeriod} onChange={setPeriod} />
      </div>

      <SetupCard setup={data?.setup} onGo={(id, sheet) => (onOpenSheet ? onOpenSheet(id, sheet) : onOpen(id))} />

      {/* stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 18 }}>
        <div style={tile}>
          <div style={tileLabel}>Collected · {monthLabel}</div>
          <div style={{ ...mono(), fontSize: 25, fontWeight: 600, color: C.ink, marginTop: 5, letterSpacing: '-.02em' }}>{inr(collected)}</div>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3 }}>of {inr(expected)} due · <b style={{ color: pct >= 100 ? C.hi : C.ink2 }}>{pct}%</b></div>
          <div style={barTrack}><span style={{ ...barFill, width: `${pct}%`, background: C.hi }} /></div>
        </div>

        <div style={tile}>
          <div style={tileLabel}>Arrears outstanding</div>
          <div style={{ ...mono(), fontSize: 25, fontWeight: 600, color: (arrears?.total ?? 0) > 0 ? C.red : C.hi, marginTop: 5, letterSpacing: '-.02em' }}>{inr(arrears?.total ?? 0)}</div>
          <AgeBars a={arrears} />
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 7 }}>aged 0–30 · 31–60 · 61–90 · <b style={{ color: C.red }}>90+</b></div>
        </div>

        <div style={tile}>
          <div style={tileLabel}>Occupancy</div>
          <div style={{ ...mono(), fontSize: 25, fontWeight: 600, color: C.ink, marginTop: 5, letterSpacing: '-.02em' }}>{stats?.occupied ?? 0}<span style={{ fontSize: 16, color: C.muted }}> / {stats?.units ?? 0} units</span></div>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3 }}>{(stats?.units ?? 0) - (stats?.occupied ?? 0)} vacant</div>
          <div style={barTrack}><span style={{ ...barFill, width: `${stats?.units ? (stats.occupied / stats.units) * 100 : 0}%`, background: C.accent }} /></div>
        </div>
      </div>

      {/* cash calendar + send today */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 18, marginBottom: 18 }}>
        <section style={panel}>
          <div style={panelHead}><h3 style={panelTitle}><Icon n="cal" size={15} /> Rent landing · {monthLabel}</h3></div>
          <div style={{ padding: '14px 16px 16px' }}>
            <RentLanding tenancies={tenancies} monthLabel={monthLabel} />
          </div>
        </section>

        <section style={panel}>
          <div style={panelHead}>
            <h3 style={panelTitle}><Icon n="chat" size={15} /> Send today {toSend.length > 0 && <span style={{ color: C.muted, fontWeight: 500 }}>· {toSend.length}</span>}</h3>
          </div>
          {toSend.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 16px', color: C.hi }}>
              <span style={{ width: 34, height: 34, borderRadius: 999, background: C.greenSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Icon n="check" size={17} /></span>
              <div><div style={{ fontSize: 13.8, fontWeight: 600, color: C.ink }}>Nothing to chase</div><div style={{ fontSize: 12.5, color: C.muted }}>Every tenant is paid up or not yet due.</div></div>
            </div>
          ) : (
            toSend.map((t, i) => {
              const di = t.dueInDays ?? 0;
              const late = di < 0;
              const when = late ? `${Math.abs(di)}d overdue` : di === 0 ? 'due today' : `due in ${di}d`;
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderTop: i ? `1px solid ${C.line2}` : 0 }}>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpen(t.id)}>
                    <b style={{ fontSize: 13.6, fontWeight: 600, color: C.ink, display: 'block' }}>{t.tenantName}</b>
                    <small style={{ fontSize: 12, color: late ? C.red : C.muted }}>
                      {inr(t.balance ?? 0)} · {when}
                    </small>
                  </div>
                  <button onClick={() => nudge(t)} title={t.tenantPhone ? 'Open WhatsApp with a reminder ready' : 'No phone on file'} style={{ ...waBtn, opacity: t.tenantPhone ? 1 : 0.5 }}>
                    <Icon n="chat" size={14} color="#fff" /> Send
                  </button>
                </div>
              );
            })
          )}
        </section>
      </div>

      {/* dates + tenancies */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.15fr .85fr', gap: 18 }}>
        <section style={panel}>
          <div style={panelHead}><h3 style={panelTitle}><Icon n="bell" size={15} /> Upcoming &amp; overdue</h3></div>
          {dates.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 16px', color: C.hi }}>
              <span style={{ width: 34, height: 34, borderRadius: 999, background: C.greenSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Icon n="check" size={17} /></span>
              <div><div style={{ fontSize: 13.8, fontWeight: 600, color: C.ink }}>All clear</div><div style={{ fontSize: 12.5, color: C.muted }}>No renewals, escalations or expiries on the horizon.</div></div>
            </div>
          ) : (
            dates.slice(0, 6).map((d, i) => {
              const dc = dateCount(d);
              return (
                <div key={d.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderTop: i ? `1px solid ${C.line2}` : 0 }}>
                  <div style={{ width: 50, flex: 'none', textAlign: 'center' }}>
                    <div style={{ ...mono(), fontSize: 16.5, fontWeight: 700, color: dc.red ? C.red : C.ink, lineHeight: 1 }}>{dc.n}</div>
                    <div style={{ fontSize: 9.4, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted }}>{dc.unit}</div>
                  </div>
                  <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 3, background: dateBarColor(d) }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.8, color: C.ink }}>{d.title}</div>
                    <div style={{ color: C.muted, fontSize: 12.3 }}>{d.subtitle}</div>
                  </div>
                </div>
              );
            })
          )}
        </section>

        <section style={panel}>
          <div style={panelHead}><h3 style={panelTitle}><Icon n="key" size={15} /> Tenancies</h3></div>
          {tenancies.length === 0 ? (
            <div style={{ padding: '22px 16px', color: C.muted, fontSize: 13 }}>No tenancies yet.</div>
          ) : (
            tenancies.map((t, i) => {
              const p = rowPill(t.status, t.dueInDays);
              return (
                <div key={t.id} onClick={() => onOpen(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: i ? `1px solid ${C.line2}` : 0, cursor: 'pointer' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ color: C.ink, fontWeight: 600, fontSize: 13.4, display: 'block' }}>{t.unitLabel} · {t.tenantName}</b>
                    <small style={{ color: C.muted, fontSize: 12 }}>{unitDesc(t)}</small>
                  </div>
                  <div style={{ ...mono(), color: C.ink, fontWeight: 600, fontSize: 13.2 }}>{inr(t.rentAmount)}</div>
                  <div style={{ width: 72, textAlign: 'right' }}><Pill kind={p.kind}>{p.label}</Pill></div>
                </div>
              );
            })
          )}
        </section>
      </div>
    </>
  );
}

/** Four proportional bars for the arrears ageing buckets. */
function AgeBars({ a }: { a?: Overview['arrears'] }) {
  const b = [a?.b0_30 ?? 0, a?.b31_60 ?? 0, a?.b61_90 ?? 0, a?.b90 ?? 0];
  const max = Math.max(1, ...b);
  const colors = [C.greenSoft, C.amberSoft, C.redSoft, C.red];
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 30, marginTop: 10 }}>
      {b.map((v, i) => (
        <div key={i} title={inr(v)} style={{ flex: 1, height: `${Math.max(8, (v / max) * 100)}%`, background: colors[i], borderRadius: '4px 4px 0 0' }} />
      ))}
    </div>
  );
}

type RentState = 'paid' | 'due' | 'overdue';
const rentStateOf = (t: TenancyRow): RentState =>
  (t.netExpected ?? 0) > 0 && (t.netReceived ?? 0) >= (t.netExpected ?? 0)
    ? 'paid'
    : (t.dueInDays ?? 0) < 0 || t.status === 'overdue'
      ? 'overdue'
      : 'due';
const RENT_ORDER: Record<RentState, number> = { paid: 0, due: 1, overdue: 2 };

/**
 * Rent this month, as one composition bar — each slice a tenant, sized by their
 * rent and coloured paid / due / overdue. Built from the active roster so the
 * total reconciles with the Collected tile. No per-tenant list (that lives in
 * Send today + Tenancies).
 */
function RentLanding({ tenancies, monthLabel }: { tenancies: TenancyRow[]; monthLabel: string }) {
  const active = tenancies.filter((t) => (t.lifecycle ?? 'active') === 'active' && t.status !== 'waived');
  const expected = active.reduce((s, t) => s + (t.netExpected ?? 0), 0);
  const collected = active.reduce((s, t) => s + (t.netReceived ?? 0), 0);
  const pct = expected > 0 ? Math.round((collected / expected) * 100) : 0;
  const paidCount = active.filter((t) => rentStateOf(t) === 'paid').length;
  const color: Record<RentState, string> = { paid: C.hi, due: C.amber, overdue: C.red };

  const segs = active
    .map((t) => ({ name: t.tenantName, amount: t.netExpected ?? 0, st: rentStateOf(t) }))
    .filter((s) => s.amount > 0)
    .sort((a, b) => RENT_ORDER[a.st] - RENT_ORDER[b.st]);

  if (active.length === 0) {
    return <div style={{ color: C.muted, fontSize: 13, padding: '10px 0' }}>No rent scheduled for {monthLabel}.</div>;
  }

  return (
    <div>
      <div style={{ ...mono(), fontSize: 23, fontWeight: 650, color: C.ink, letterSpacing: '-.01em' }}>
        {inr(collected)} <span style={{ fontSize: 14, color: C.muted, fontWeight: 500, fontFamily: 'inherit' }}>of {inr(expected)} · {pct}%</span>
      </div>
      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2, marginBottom: 14 }}>{paidCount} of {active.length} paid</div>

      <div style={{ display: 'flex', gap: 2, height: 16, borderRadius: 6, overflow: 'hidden' }}>
        {segs.map((s, i) => (
          <div key={i} title={`${s.name} · ${inr(s.amount)} · ${s.st}`} style={{ width: `${(s.amount / expected) * 100}%`, background: color[s.st] }} />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 12, color: C.muted }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: C.hi, display: 'inline-block' }} /> paid</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: C.amber, display: 'inline-block' }} /> due</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: C.red, display: 'inline-block' }} /> overdue</span>
      </div>
    </div>
  );
}

const tile: React.CSSProperties = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: '14px 15px' };
const tileLabel: React.CSSProperties = { fontSize: 11.5, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' };
const barTrack: React.CSSProperties = { height: 7, borderRadius: 99, background: C.line2, overflow: 'hidden', marginTop: 11 };
const barFill: React.CSSProperties = { display: 'block', height: '100%', borderRadius: 99 };
const panel: React.CSSProperties = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' };
const panelHead: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: `1px solid ${C.line2}` };
const panelTitle: React.CSSProperties = { fontSize: 15, margin: 0, fontWeight: 650, display: 'flex', alignItems: 'center', gap: 8 };
const waBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#128c4b', color: '#fff', border: 0, borderRadius: 8, padding: '7px 12px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', flex: 'none' };
const retryBtn: React.CSSProperties = { marginTop: 4, border: `1px solid ${C.line}`, background: C.card, color: C.accent, fontWeight: 600, fontSize: 13, padding: '8px 16px', borderRadius: 9, cursor: 'pointer' };
